import React, { KeyboardEvent } from 'react';
import { Mic, MicOff, Send, Loader2 } from 'lucide-react';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isProcessing: boolean;
  voiceProps?: {
    isListening: boolean;
    onStartListening: () => void;
    onStopListening: () => void;
  };
}

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  isProcessing,
  voiceProps,
}) => {
  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="relative flex items-center gap-2 p-2 bg-surface-dark rounded-xl border border-border">
      {/* Voice button */}
      {voiceProps && (
        <button
          onClick={voiceProps.isListening ? voiceProps.onStopListening : voiceProps.onStartListening}
          className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
            voiceProps.isListening
              ? 'text-primary bg-primary/10'
              : 'text-text-secondary hover:text-primary hover:bg-surface'
          }`}
          title={voiceProps.isListening ? 'Stop listening' : 'Start listening'}
        >
          {voiceProps.isListening ? (
            <MicOff className="w-5 h-5" />
          ) : (
            <Mic className="w-5 h-5" />
          )}
        </button>
      )}

      {/* Textarea */}
      <div className="relative flex-1">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Digite sua mensagem..."
          className="w-full bg-transparent text-white placeholder:text-white-muted resize-none min-h-[20px] max-h-32 focus:outline-none"
          style={{
            height: '20px',
            maxHeight: '128px',
          }}
          disabled={isProcessing}
        />
      </div>

      {/* Send button */}
      <button
        onClick={onSend}
        disabled={isProcessing || !value.trim()}
        className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
          isProcessing
            ? 'opacity-50 cursor-not-allowed'
            : value.trim()
            ? 'text-primary bg-primary/10 hover:bg-primary/20'
            : 'text-text-secondary hover:text-primary hover:bg-surface'
        }`}
        title="Send message"
      >
        {isProcessing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Send className="w-5 h-5" />
        )}
      </button>
    </div>
  );
};

export default ChatInput;